import React, { FC, useState, useEffect } from 'react';
import Image, { FluidObject } from 'gatsby-image';
import styled from 'styled-components';
import Box from 'lightbox-react';
import 'lightbox-react/style.css'; // This only needs to be imported once in your app
import Video from './Video';
import { IFluidObject } from 'gatsby-background-image';
import useInterval from '../hooks/useInterval';
import Icon from './Icon';

type LightboxProps = {
  images: IFluidObject[];
  youtubeUrls: string[];
  galleryButtonVisible: boolean;
  className?: string;
  autoPlaying?: boolean;
  startAutoplayingInstantly?: boolean;
  showLeftRightButtons?: boolean;
  startAtNextPhoto?: boolean;
  onMouseEnter?: () => void;
};

const Container = styled.div``;

const ImageContainer = styled.div`
  position: relative;
  margin: auto;
  cursor: pointer;
`;

const GalleryButton = styled.button`
  background: none;
  border: none;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  cursor: pointer;
  transform: translate3d(-50%, -50%, 0);
  > svg {
  }
`;

const Lightbox: FC<LightboxProps> = ({
  images,
  youtubeUrls,
  galleryButtonVisible,
  className = '',
  autoPlaying = false,
  startAutoplayingInstantly = false,
  onMouseEnter,
  showLeftRightButtons = false,
  startAtNextPhoto = false,
}) => {
  const [galleryContent, setGalleryContent] = useState<any[]>([]);
  const [showLightbox, setShowLightbox] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    if (startAtNextPhoto) {
      setGalleryContent([
        ...youtubeUrls.map(url => <Video key={url} url={url.replace('watch?v=', 'embed/')} />),
        ...images.slice(1).map(img => img.src),
      ]);
    } else {
      setGalleryContent([
        ...images.map(img => img.src),
        ...youtubeUrls.map(url => <Video key={url} url={url.replace('watch?v=', 'embed/')} />),
      ]);
    }
  }, [images]);

  useEffect(() => {
    if (autoPlaying && startAutoplayingInstantly) {
      setPhotoIndex((photoIndex + 1) % galleryContent.length);
    }
  }, [autoPlaying, startAutoplayingInstantly]);

  useInterval(() => {
    if (autoPlaying && !showLightbox) {
      setPhotoIndex((photoIndex + 1) % galleryContent.length);
    }
  }, 5000);

  return (
    <>
      <Container className={className}>
        <ImageContainer
          onMouseEnter={onMouseEnter}
          onClick={() => {
            setShowLightbox(true);
            setPhotoIndex(0);
          }}
        >
          <Image style={{ position: 'relative' }} fluid={images[photoIndex] as FluidObject} />
          {galleryButtonVisible && (
            <GalleryButton type="button">
              <svg
                className="text-primary fill-current"
                xmlns="http://www.w3.org/2000/svg"
                width="100"
                height="100"
                viewBox="0 0 24 24"
              >
                <path d="M19 12h-2v3h-3v2h5v-5zM7 9h3V7H5v5h2V9zm14-6H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16.01H3V4.99h18v14.02z" />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
            </GalleryButton>
          )}
          {showLeftRightButtons && galleryContent.length > 1 && (
            <div className="h-full w-full absolute z-0 top-0 flex items-center justify-between">
              <Icon
                onClick={e => {
                  e.stopPropagation();
                  setPhotoIndex((photoIndex + galleryContent.length - 1) % galleryContent.length);
                }}
                className="w-16 h-16 -ml-2"
                name="arrow_left"
              />
              <Icon
                onClick={e => {
                  e.stopPropagation();
                  setPhotoIndex((photoIndex + 1) % galleryContent.length);
                }}
                className="w-16 h-16 -mr-2"
                name="arrow_right"
              />
            </div>
          )}
        </ImageContainer>

        {showLightbox && (
          <Box
            mainSrc={galleryContent[photoIndex]}
            nextSrc={galleryContent[(photoIndex + 1) % galleryContent.length]}
            prevSrc={galleryContent[(photoIndex + galleryContent.length - 1) % galleryContent.length]}
            onCloseRequest={() => {
              setPhotoIndex(0);
              setShowLightbox(false);
            }}
            onMovePrevRequest={() => setPhotoIndex((photoIndex + galleryContent.length - 1) % galleryContent.length)}
            onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % galleryContent.length)}
          />
        )}
      </Container>
    </>
  );
};

export default Lightbox;
